import { ReactElement, useMemo } from "react";
import * as Yup from "yup";

import {
  ALLOWED_MMS_ATTACHMENT_CONTENT_TYPES,
  getMimeTypeFromUrl,
} from "@stw/mime-types";

import { Upload as UploadType } from "~frontend/__generated__/graphql";

import MediaAttachment from "../MediaAttachment";

const MEDIA_FILE_MAX_SIZE_BYTES = 500000;

export type UploadFileFields = Pick<
  UploadType,
  "id" | "fileName" | "publicGetUrl" | "contentType"
>;

export type MediaFieldValue =
  | { file: File; upload?: null | undefined; url?: null | undefined }
  | {
      file?: null | undefined;
      upload: UploadFileFields;
      url?: null | undefined;
    }
  | {
      file?: null | undefined;
      upload?: null | undefined;
      url: string;
    }
  | null;

export function MediaPreview({
  media,
}: {
  media: MediaFieldValue;
}): ReactElement | null {
  const urlAndType = useMemo(() => {
    if (media?.file) {
      return { url: URL.createObjectURL(media.file), type: media.file.type };
    } else if (media?.upload) {
      return {
        url: media.upload.publicGetUrl!,
        type: media.upload.contentType,
      };
    } else if (media?.url) {
      const mimeType = getMimeTypeFromUrl(media.url);
      return mimeType && ALLOWED_MMS_ATTACHMENT_CONTENT_TYPES.includes(mimeType)
        ? {
            url: media.url,
            type: mimeType,
          }
        : null;
    }
    return null;
  }, [media]);

  if (urlAndType) {
    return (
      <MediaAttachment src={urlAndType.url} contentType={urlAndType.type} />
    );
  }

  return null;
}

export function mediaValidationSchema(): Yup.AnySchema {
  return Yup.mixed().test(
    "file-size",
    `File must be smaller than ${(
      MEDIA_FILE_MAX_SIZE_BYTES / 1000
    ).toLocaleString()} KB`,
    (media: MediaFieldValue) =>
      !media?.file || media.file.size < MEDIA_FILE_MAX_SIZE_BYTES
  );
}
